/**
 * AdminPage banner — surfaces the LeagueSquad.unresolvedPlayerWarnings field
 * that the recompute path writes when a squad player can't be resolved by id
 * or canonical name in the current pool. Without this banner, the warnings
 * live only in Firestore and admins never see them.
 *
 * Renders nothing when every squad in every tennis league is clean. When
 * something's wrong, shows a red Alert at the top of the page with an
 * expandable per-league / per-squad / per-warning breakdown.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Chip, Collapse, IconButton, Stack, Typography,
} from '@mui/material';
import { ExpandMore, ExpandLess, ReportProblem } from '@mui/icons-material';
import type { League } from '../../types/database';
import { tennisSquadService } from '../../services/firestore';
import {
  summarizeUnresolvedSquadWarnings,
  type UnresolvedSquadDigest,
} from '../../utils/unresolvedSquadWarnings';

type Props = { leagues: League[] };

const UnresolvedSquadsBanner: React.FC<Props> = ({ leagues }) => {
  const [digest, setDigest] = useState<UnresolvedSquadDigest>({
    totalSquads: 0, totalWarnings: 0, perLeague: [],
  });
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const tennisLeagues = leagues.filter((l) => l.sport === 'tennis');
      if (tennisLeagues.length === 0) {
        if (!cancelled) setDigest({ totalSquads: 0, totalWarnings: 0, perLeague: [] });
        return;
      }
      setLoading(true);
      try {
        const bundle = await Promise.all(
          tennisLeagues.map(async (league) => ({
            league,
            squads: await tennisSquadService.getByLeague(league.id),
          })),
        );
        if (cancelled) return;
        setDigest(summarizeUnresolvedSquadWarnings(bundle));
      } catch (e) {
        console.error('UnresolvedSquadsBanner load failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [leagues]);

  if (loading || digest.totalSquads === 0) return null;

  return (
    <Alert
      severity="error"
      icon={<ReportProblem />}
      sx={{ mb: 2 }}
      action={
        <IconButton size="small" onClick={() => setExpanded((v) => !v)} aria-label="toggle details">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      }
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {digest.totalSquads} squad{digest.totalSquads === 1 ? '' : 's'} with unresolved players
      </AlertTitle>
      <Typography variant="body2">
        {digest.totalWarnings} warning{digest.totalWarnings === 1 ? '' : 's'} across{' '}
        {digest.perLeague.length} league{digest.perLeague.length === 1 ? '' : 's'} — the
        recompute couldn&apos;t link a stored squad player to any current pool entry.
        Likely cause: a pool entry got recreated under a new playerId and the canonical
        name is ambiguous or missing.
      </Typography>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1.5 }}>
          {digest.perLeague.map((lg) => (
            <Box key={lg.leagueName} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {lg.leagueName}
              </Typography>
              <Stack spacing={0.75}>
                {lg.squads.map((sq) => (
                  <Box key={sq.squadId} sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'error.main' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip size="small" label={sq.squadName} color="error" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        squad id: {sq.squadId}
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.25 }}>
                      {sq.warnings.map((w, i) => (
                        <li key={i}>
                          <Typography variant="caption">{w}</Typography>
                        </li>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
};

export default UnresolvedSquadsBanner;
